import { computeBalanceSheet } from '../../utils/balanceSheet';

/**
 * Build the monthly closing report. Pure function — no state mutation.
 * Returns null when not on a 30-day boundary.
 *
 * Inputs:
 *   state              — current store snapshot (read-only)
 *   driversAfterLease  — drivers post lease-end cleanup
 *   finalVehicles      — vehicles post lease-end cleanup
 *   finalTreasury      — treasury after agios
 *   newMonth, newYear  — calendar after the tick
 */
export function buildMonthlyReport({
  state,
  driversAfterLease,
  finalVehicles,
  finalTreasury,
  newMonth,
  newYear,
  expenseSnapshot = null,
}) {
  if (!((state.totalDays + 1) % 30 === 0 && state.totalDays > 1)) return null;

  const last30 = state.treasuryHistory.slice(-30);
  const totalRev = last30.reduce((s, h) => s + (h.revenue || 0), 0);
  const totalExp = last30.reduce((s, h) => s + (h.expenses || 0), 0);
  const bestDriver = driversAfterLease.length > 0
    ? driversAfterLease.reduce((a, b) => (a.satisfaction > b.satisfaction ? a : b))
    : null;
  const busyVehicles = finalVehicles.filter(v => v.status === 'active').length;

  // Snapshot balance sheet at month close for the Capitaux trend chart.
  const _snapshotState = {
    treasury: finalTreasury,
    vehicles: finalVehicles,
    credits: state.credits || [],
    activePlacements: state.activePlacements || [],
    totalDays: state.totalDays + 1,
    tvaCollectedTotal: state.tvaCollectedTotal || 0,
    tvaDeductibleTotal: state.tvaDeductibleTotal || 0,
    annualProfit: state.annualProfit || 0,
  };
  const _sheet = computeBalanceSheet(_snapshotState);

  // Placements (financial investments) — sum the capital invested + an
  // estimated accumulated return, so the supervisor / player sees a
  // proper "Capitaux placés" line in the monthly report.
  const placementsCapital = (state.activePlacements || []).reduce(
    (s, p) => s + (p.amount || 0),
    0
  );
  const placementsReturn = (state.activePlacements || []).reduce(
    (s, p) => s + ((p.amount || 0) * (p.annualRate || 0) / 12 * Math.max(0, (state.totalDays + 1 - (p.startDay || 0)) / 30)),
    0
  );

  // 22/07/2026 — bug « panier moyen à 453 268 € » : l'ancien compteur ne
  // comptait que les contrats finissant LE dernier jour du mois
  // (daysRemaining <= 1 à la clôture). On compte désormais tous les
  // contrats livrés dans la fenêtre de 30 jours via le contractLedger.
  const _dayEnd = state.totalDays + 1;
  const _ledger = state.contractLedger || [];
  const _completedThisMonth = _ledger.filter(e =>
    e.status === 'completed' && (e.completedOnDay || 0) > _dayEnd - 30 && (e.completedOnDay || 0) <= _dayEnd
  ).length;
  const contractsCompleted = _ledger.length > 0
    ? _completedThisMonth
    : state.activeContracts.filter(c => c.daysRemaining <= 1).length;

  // 22/07/2026 — détail des charges par poste (retour user). Le snapshot
  // vient de coreSlice (coûts mensuels au moment de la clôture) ; la ligne
  // « autres » absorbe l'écart avec le total réellement débité (événements,
  // amendes, intérim, frais à l'étranger, comptabilité, exceptionnels).
  let expensesByPoste = null;
  if (expenseSnapshot) {
    const m = expenseSnapshot.expenseMultiplier || 1;
    const r = (v) => Math.round((v || 0) * m);
    expensesByPoste = {
      salaires: r(expenseSnapshot.salaires),
      fonctionsSupport: r(expenseSnapshot.support),
      managersDepots: r(expenseSnapshot.managers),
      carburant: r(expenseSnapshot.carburant),
      peages: r(expenseSnapshot.peages),
      entretien: r(expenseSnapshot.entretien),
      assurances: r(expenseSnapshot.assurances),
      leasing: r(expenseSnapshot.leasing),
      credits: r(expenseSnapshot.credits),
      investissements: r(expenseSnapshot.investissements),
      loyersEtDepots: r((expenseSnapshot.fixed || 0) + (expenseSnapshot.depots || 0)
        + (expenseSnapshot.bureau || 0) + (expenseSnapshot.loyers || 0)),
      // 28/09/2026 — frais de structure (taille de flotte).
      fraisStructure: r(expenseSnapshot.structure),
    };
    const _sumPostes = Object.values(expensesByPoste).reduce((a, b) => a + b, 0);
    expensesByPoste.autres = Math.max(0, Math.round(totalExp) - _sumPostes);
  }

  return {
    month: newMonth === 1 ? 12 : newMonth - 1,
    year: newMonth === 1 ? newYear - 1 : newYear,
    totalRevenue: Math.round(totalRev),
    totalExpenses: Math.round(totalExp),
    netResult: Math.round(totalRev - totalExp),
    treasury: Math.round(finalTreasury),
    vehiclesActive: busyVehicles,
    vehiclesTotal: finalVehicles.length,
    driversTotal: driversAfterLease.length,
    bestDriverName: bestDriver?.name || '—',
    bestDriverSatisfaction: bestDriver ? Math.round(bestDriver.satisfaction) : 0,
    contractsCompleted,
    reputation: Math.round(state.reputation),
    // 22/07/2026 — détail des charges par poste (null pour les vieux mois).
    expensesByPoste,
    // === Balance-sheet snapshot (used by the Capitaux trend chart) ===
    equity: _sheet.equity,
    actifTotal: _sheet.actif.total,
    passifTotal: _sheet.passif.total,
    fleetNet: _sheet.fleet.netValue,
    solvencyPct: _sheet.solvencyPct,
    // === Patrimoine & placements (P1#8 feedback 26 mai) ===
    placementsCapital: Math.round(placementsCapital),
    placementsReturn: Math.round(placementsReturn),
    placementsCount: (state.activePlacements || []).length,
    totalDebt: Math.round((state.credits || []).reduce((s, c) => s + (c.remainingPrincipal || 0), 0)),
    monthlyInterest: Math.round((state.credits || []).reduce((s, c) => s + ((c.remainingPrincipal || 0) * (c.annualRate || 0) / 12), 0)),
  };
}
